import { downloadStoredFile } from "@/lib/directDownload";
import { useEffect, useRef, useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/axios";
import { isTrustedApiUrl, openNoOpener } from "@/lib/security";
import { resolveConfiguredApiUrl } from "@/lib/apiUrl";
import { toast } from "sonner";
import {
  MAX_POLL_DURATION_MS,
  removePendingExport,
  savePendingExport,
} from "@/components/analytics/exportJobStorage";

// ── Polling config ──────────────────────────────────────────────────────
const INITIAL_INTERVAL_MS = 5_000;
const MAX_INTERVAL_MS = 30_000;
const BACKOFF_FACTOR = 2;
const SILENT_RETRY_THRESHOLD = 3; // suppress console until N consecutive failures

// ── Types ───────────────────────────────────────────────────────────────
type ExportJobStatus = "pending" | "processing" | "completed" | "failed";

interface ExportJobPollerProps {
  jobId: string;
  statusUrl: string;
  layerName?: string;
  format?: string;
  onDownloadReady: (url: string, filename: string) => void;
}

const getFallbackExtension = (format?: string) => {
  switch ((format || "").toLowerCase()) {
    case "excel":
    case "xlsx":
      return ".zip";
    case "docx":
      return ".docx";
    case "pdf":
      return ".pdf";
    case "geojson":
      return ".geojson";
    case "shapefile":
    case "shp":
      return ".zip";
    case "geopackage":
    case "geopkg":
      return ".gpkg";
    case "kml":
      return ".kml";
    case "kmz":
      return ".kmz";
    case "zip":
    case "complete":
      return ".zip";
    default:
      return ".zip";
  }
};

/**
 * Polls /api/exports/status/{jobId}/ with exponential backoff until the
 * Celery job completes, then shows a download button and fires a toast.
 */
export const ExportJobPoller = ({
  jobId,
  statusUrl,
  layerName,
  format,
  onDownloadReady,
}: ExportJobPollerProps) => {
  const [status, setStatus] = useState<ExportJobStatus>("pending");
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [progress, setProgress] = useState(0);
  const [downloading, setDownloading] = useState(false);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);
  const intervalRef = useRef(INITIAL_INTERVAL_MS);
  const consecutiveFailsRef = useRef(0);
  const startTimeRef = useRef<number>(null!);
  if (startTimeRef.current === null) startTimeRef.current = Date.now();
  const downloadNotifiedRef = useRef(false);
  const onDownloadReadyRef = useRef(onDownloadReady);
  useEffect(() => {
    onDownloadReadyRef.current = onDownloadReady;
  }, [onDownloadReady]);

  // ── Save to localStorage on mount ─────────────────────────────────────
  useEffect(() => {
    savePendingExport({
      jobId,
      statusUrl,
      layerName,
      format,
      startedAt: Date.now(),
    });
  }, [jobId, statusUrl, layerName, format]);

  const stopPolling = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    removePendingExport(jobId);
  }, [jobId]);

  const schedulePoll = useCallback(
    (fn: () => void) => {
      // Check max duration
      if (Date.now() - startTimeRef.current > MAX_POLL_DURATION_MS) {
        setStatus("failed");
        setError("El tiempo de espera se ha agotado (10 min).");
        stopPolling();
        return;
      }
      timeoutRef.current = setTimeout(fn, intervalRef.current);
      // Exponential backoff
      intervalRef.current = Math.min(
        intervalRef.current * BACKOFF_FACTOR,
        MAX_INTERVAL_MS
      );
    },
    [stopPolling]
  );

  const pollStatus = useCallback(async () => {
    if (!mountedRef.current) return;
    try {
      const { data } = await api.get(resolveConfiguredApiUrl(statusUrl));
      if (!mountedRef.current) return;

      consecutiveFailsRef.current = 0; // reset on success
      // Reset backoff on success (fast polling while server is responsive)
      intervalRef.current = INITIAL_INTERVAL_MS;

      const jobStatus: ExportJobStatus =
        data?.data?.status ?? data?.status ?? "pending";
      const jobProgress: number = data?.data?.progress ?? data?.progress ?? 0;

      setStatus(jobStatus);
      setProgress(jobProgress);

      if (jobStatus === "completed") {
        stopPolling();

        const url =
          data?.data?.download_url ??
          data?.download_url ??
          `/exports/download/${jobId}`;
        setDownloadUrl(url);

        const ext = getFallbackExtension(format);
        const filename = `reporte_${layerName || "datos"}${ext}`;
        if (!downloadNotifiedRef.current) {
          downloadNotifiedRef.current = true;
          onDownloadReadyRef.current(url, filename);
        }

        // Toast notification
        toast.success("Reporte listo para descargar", {
          description: `${layerName || "Datos"} — ${format?.toUpperCase() || "PDF"}`,
          action: {
            label: "Descargar",
            onClick: () => handleDownloadDirect(url, filename),
          },
          duration: 10_000,
        });
        return;
      }

      if (jobStatus === "failed") {
        stopPolling();
        const errMsg =
          data?.data?.error_message ??
          data?.error_message ??
          "Error al generar el reporte.";
        setError(errMsg);
        toast.error("Error generando reporte", { description: errMsg });
        return;
      }

      // Still pending/processing — schedule next poll
      schedulePoll(pollStatus);
    } catch {
      consecutiveFailsRef.current += 1;
      if (consecutiveFailsRef.current >= SILENT_RETRY_THRESHOLD) {
      }
      // Don't stop — schedule retry with backoff
      if (mountedRef.current) {
        schedulePoll(pollStatus);
      }
    }
  }, [statusUrl, jobId, format, layerName, stopPolling, schedulePoll]);

  useEffect(() => {
    mountedRef.current = true;
    startTimeRef.current = Date.now();

    // First poll immediately
    pollStatus();

    // Elapsed timer
    timerRef.current = setInterval(() => {
      setElapsed((prev) => prev + 1);
    }, 1000);

    return () => {
      mountedRef.current = false;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [pollStatus]);

  const handleDownloadDirect = async (url: string, _fname: string) => {
    setDownloading(true);
    try {
      const resolved = resolveConfiguredApiUrl(url);
      const isExternal =
        resolved.startsWith("https://") && !isTrustedApiUrl(resolved);
      if (isExternal) {
        openNoOpener(resolved);
        return;
      }
      await downloadStoredFile(resolved);
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("Error al descargar el archivo");
    } finally {
      setDownloading(false);
    }
  };

  const handleDownload = () => {
    if (!downloadUrl) return;
    const ext =
      format === "excel" ? ".zip" : format === "docx" ? ".docx" : ".pdf";
    handleDownloadDirect(downloadUrl, `reporte_${layerName || "datos"}${ext}`);
  };

  const formatElapsed = () => {
    const mins = Math.floor(elapsed / 60);
    const secs = elapsed % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  // ── Completed ─────────────────────────────────────────────────────────
  if (status === "completed" && downloadUrl) {
    return (
      <Card className="mt-2 border-green-500/30 bg-green-500/5">
        <CardContent className="flex items-center gap-3 p-3">
          <CheckCircle2 className="size-4 text-green-600 shrink-0" />
          <span className="text-xs font-medium text-green-700">
            Reporte listo
          </span>
          <Button
            size="sm"
            variant="outline"
            className="gap-2 h-7 text-xs ml-auto"
            onClick={handleDownload}
            disabled={downloading}
          >
            {downloading ? (
              <>
                <Loader2 className="size-3.5 animate-spin" />
                Descargando…
              </>
            ) : (
              <>
                <Download className="size-3.5" />
                Descargar
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // ── Failed ────────────────────────────────────────────────────────────
  if (status === "failed") {
    return (
      <Card className="mt-2 border-destructive/30 bg-destructive/5">
        <CardContent className="flex items-center gap-3 p-3">
          <AlertCircle className="size-4 text-destructive shrink-0" />
          <span className="text-xs text-destructive">
            {error || "Error al generar el reporte."}
          </span>
        </CardContent>
      </Card>
    );
  }

  // ── Pending / Processing ──────────────────────────────────────────────
  return (
    <Card className="mt-2 border-primary/20 bg-primary/5">
      <CardContent className="flex flex-col gap-2 p-3">
        <div className="flex items-center gap-3">
          <Loader2 className="size-4 animate-spin text-primary shrink-0" />
          <div className="flex flex-col gap-0.5 flex-1">
            <span className="text-xs font-medium">
              Generando reporte{layerName ? ` de ${layerName}` : ""}...
            </span>
            <span className="text-[10px] text-muted-foreground">
              {formatElapsed()} : recibirás el archivo automáticamente
            </span>
          </div>
        </div>
        {progress > 0 && progress < 100 && (
          <div className="w-full bg-muted rounded-full h-1.5 overflow-hidden">
            <div
              className="bg-primary h-full rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};
